// ============================================
// Chat Module - Actions (ES6)
// 세션 구조 전용 (레거시 chats 컬렉션 제거됨)
// ============================================

import { CHAT_ENDPOINTS } from '../shared/api-endpoints.js';
import { authFetch } from '../shared/auth-fetch.js';
import { removeRoomFromList, invalidateChatCache, loadChatList } from './list.js';
import { closeChatOverlay, showNoChatState } from './overlay.js';
import {
  getCurrentRoomId,
  getCurrentSessionId,
  setCurrentRoomId,
  setCurrentSessionId,
  setCurrentChatData,
  setCurrentProfile
} from '../app/state.js';
import { clearMessages } from './messaging/renderer.js';
import { showToast, showGlobalLoading, hideGlobalLoading } from '../utils.js';
import { ModalManager } from '../modal-manager.js';
import { showConfirm } from '../shared/confirm-modal.js';
import { showSessionModal } from './session-modal.js';

// ============================================
// 선택된 룸 상태
// ============================================
let selectedRoomForAction = null;

// ============================================
// Room Action Modal (세션 구조용)
// ============================================

export function openRoomActionModal(roomId, title, isHidden) {
  selectedRoomForAction = { id: roomId, hidden: isHidden, title };
  document.getElementById('chatActionTitle').textContent = title;
  document.getElementById('hideActionText').textContent = isHidden ? '숨기기 해제' : '숨기기';

  // 세션 관리 버튼 표시 (있으면)
  const sessionBtn = document.getElementById('sessionManageBtn');
  if (sessionBtn) sessionBtn.style.display = 'flex';

  const modal = document.getElementById('chatActionModal');
  requestAnimationFrame(() => {
    modal.classList.add('open');
    ModalManager.push('chatActionModal');
  });
}

export function closeRoomActionModal() {
  document.getElementById('chatActionModal').classList.remove('open');
  selectedRoomForAction = null;
  // 세션 관리 버튼 숨기기
  const sessionBtn = document.getElementById('sessionManageBtn');
  if (sessionBtn) sessionBtn.style.display = 'none';
  ModalManager.remove('chatActionModal', true);
}

// 세션 관리 열기
export function openSessionManage() {
  if (!selectedRoomForAction) return;
  const { id, title } = selectedRoomForAction;
  closeRoomActionModal();
  showSessionModal(id, title);
}

// Room 삭제 (모든 세션 삭제)
export async function confirmDeleteRoom() {
  if (!selectedRoomForAction) return;

  const confirmed = await showConfirm({
    message: '이 점술가의 모든 세션을 삭제하시겠습니까?',
    confirmText: '모두 삭제',
    isDanger: true
  });

  if (confirmed) {
    const roomId = selectedRoomForAction.id;
    closeRoomActionModal();
    await deleteRoom(roomId);
  }
}

async function deleteRoom(roomId) {
  showGlobalLoading();
  try {
    // 모든 세션 삭제 → room도 자동 삭제
    const response = await authFetch(CHAT_ENDPOINTS.deleteSession, {
      body: { roomId, deleteAllSessions: true }
    });

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || '삭제 실패');
    }

    const currentRoomId = getCurrentRoomId();
    if (currentRoomId === roomId) {
      setCurrentRoomId(null);
      setCurrentSessionId(null);
      setCurrentProfile(null);
      setCurrentChatData(null);
      clearMessages();
      const chatTitle = document.getElementById('chatTitle');
      if (chatTitle) chatTitle.textContent = '';
      showNoChatState();
      closeChatOverlay();
    }

    showToast('채팅이 삭제되었습니다');
    removeRoomFromList(roomId);
  } catch (e) {
    showToast('삭제 실패: ' + e.message, true);
  } finally {
    hideGlobalLoading();
  }
}

// ============================================
// 채팅 숨기기/표시
// ============================================

export async function toggleHideChat() {
  if (!selectedRoomForAction) return;

  try {
    showGlobalLoading();
    const newHiddenState = !selectedRoomForAction.hidden;
    const body = { roomId: selectedRoomForAction.id, hidden: newHiddenState };

    const response = await authFetch(CHAT_ENDPOINTS.toggleChatHidden, { body });

    if (!response.ok) throw new Error('서버 오류');

    showToast(newHiddenState ? '채팅이 숨겨졌습니다' : '채팅이 다시 표시됩니다');

    closeRoomActionModal();
    invalidateChatCache();
    loadChatList(0, true);
  } catch (e) {
    showToast('오류: ' + e.message, true);
  } finally {
    hideGlobalLoading();
  }
}

// ============================================
// 세션 삭제
// ============================================

export async function confirmDeleteFromModal() {
  if (!selectedRoomForAction) return;
  await confirmDeleteRoom();
}

// 단일 세션 삭제 (채팅 설정 모달에서 호출)
export async function deleteSession(roomId, sessionId, skipConfirm = false) {
  if (!roomId || !sessionId) return;

  if (!skipConfirm) {
    const confirmed = await showConfirm({
      message: '이 세션과 모든 기억을 삭제할까요?',
      confirmText: '삭제',
      isDanger: true
    });
    if (!confirmed) return;
  }

  showGlobalLoading();
  try {
    const response = await authFetch(CHAT_ENDPOINTS.deleteSession, {
      body: { roomId, sessionId }
    });

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || '삭제 실패');
    }

    const currentRoomId = getCurrentRoomId();
    const currentSessionId = getCurrentSessionId();
    if (currentRoomId === roomId && currentSessionId === sessionId) {
      setCurrentRoomId(null);
      setCurrentSessionId(null);
      setCurrentProfile(null);
      setCurrentChatData(null);
      clearMessages();
      const chatTitle = document.getElementById('chatTitle');
      if (chatTitle) chatTitle.textContent = '';
      const profileBadge = document.getElementById('currentProfileBadge');
      if (profileBadge) profileBadge.style.display = 'none';
      showNoChatState();
      closeChatOverlay();
    }

    showToast('세션이 삭제되었습니다');

    // 모든 세션이 삭제되면 room도 삭제됨
    if (result.roomDeleted) {
      removeRoomFromList(roomId);
    } else {
      invalidateChatCache();
      loadChatList(0, true);
    }
  } catch (e) {
    showToast('삭제 실패: ' + e.message, true);
  } finally {
    hideGlobalLoading();
  }
}

// ============================================
// window 노출 제거됨 (2026-01-06)
// 모든 호출이 ES6 import로 처리됨
// ============================================
